import Phaser from "phaser";
import {
  DEFAULT_BACKGROUND_ID,
  DEFAULT_DEBUG_MODE,
  NATIVE_HEIGHT,
  NATIVE_WIDTH,
  PALETTE,
  UGC_CYAN,
} from "../config";
import type { Variant } from "../config";
import type { PlayerProfile, SessionConfig } from "../types";
import type { WalletSnapshot } from "../services/Wallet";
import { AssetService } from "../services/AssetService";
import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { InputService } from "../services/InputService";
import { ProfileService } from "../services/ProfileService";
import { TelemetryService } from "../services/TelemetryService";
import { AuthorBadge } from "../ui/AuthorBadge";
import { LanguageService, t } from "../i18n";
import type { Language } from "../i18n";

// Module-level guard: the boot-time i18n.language.changed event is logged
// exactly once per page load, even if the Launcher scene restarts later
// (which happens on every language toggle).
let bootLanguageReported = false;

// PLAY (left) + MARKET (right) share these dimensions and sit side-by-side as
// one centred row. BUTTON_GROUP_OFFSET is each button's centre distance from
// screen-centre so the pair stays centred (with a small gap between them).
const BUTTON_WIDTH = 120;
const BUTTON_HEIGHT = 22;
const BUTTON_GROUP_OFFSET = (BUTTON_WIDTH + 14) / 2;

/**
 * One focusable element in the launcher's keyboard/gamepad ring. `center` and
 * `visible` are closures so they track runtime changes (PLAY's X shifts with
 * the variant; MARKET only exists in Variant B).
 */
interface LauncherFocusItem {
  center: () => { x: number; y: number };
  size: { w: number; h: number };
  visible: () => boolean;
  activate: () => void;
}

/**
 * View 1 — Launcher / Variant Select (ui-sketches §1).
 *
 * Operator-facing: pick the variant and edit the player profile, then Start.
 * Trip-length picker is stubbed at the PRD §5 default (10 min) and not
 * interactive in v0.3. The runner always starts on the default background —
 * the curated Background-Upload picker was removed (it wasn't used), so
 * `SessionConfig.backgroundId` is always `DEFAULT_BACKGROUND_ID`.
 */
export class LauncherScene extends Phaser.Scene {
  private controls!: InputService;
  private telemetry!: TelemetryService;
  private identityService!: AuthorIdentityService;
  private selectedVariant: Variant = "B";
  private participantId = "P-0001";
  private debugMode: boolean = DEFAULT_DEBUG_MODE;

  private variantA!: Phaser.GameObjects.Rectangle;
  private variantALabel!: Phaser.GameObjects.Text;
  private variantB!: Phaser.GameObjects.Rectangle;
  private variantBLabel!: Phaser.GameObjects.Text;
  private startButton!: Phaser.GameObjects.Rectangle;
  private startLabel!: Phaser.GameObjects.Text;

  // Keyboard / gamepad focus ring over every interactive element. A roaming
  // outline cursor marks the focused one; WASD / arrows move it, J / K / Space
  // (and gamepad A/X/Y/Start) activate it.
  private focusItems: LauncherFocusItem[] = [];
  private focusCursor?: Phaser.GameObjects.Rectangle;
  private focusIdx = 0;
  private prevMoveX = 0;
  private prevMoveY = 0;

  // Compact player-profile chip (participant row); click opens the editor.
  private profileBadge!: AuthorBadge;

  // Debug-mode checkbox (operator UI; default driven by VITE_DEBUG_MODE env).
  private debugCheckbox!: Phaser.GameObjects.Rectangle;
  private debugCheckmark!: Phaser.GameObjects.Text;
  private debugLabel!: Phaser.GameObjects.Text;

  // MARKET tile — Variant B only entry into ShopScene.
  private marketTile!: Phaser.GameObjects.Container;

  // Language toggle (top-right) — DE / EN chips.
  private langChipDe!: Phaser.GameObjects.Rectangle;
  private langChipDeLabel!: Phaser.GameObjects.Text;
  private langChipEn!: Phaser.GameObjects.Rectangle;
  private langChipEnLabel!: Phaser.GameObjects.Text;

  private langUnsubscribe?: () => void;

  constructor() {
    super("launcher");
  }

  create(): void {
    this.controls = new InputService(this);
    this.telemetry = new TelemetryService(this.participantId);
    this.telemetry.emit(1, "scene.enter", { scene: "launcher" });
    if (!bootLanguageReported) {
      bootLanguageReported = true;
      LanguageService.onBoot((ev) => {
        this.telemetry.emit(1, "i18n.language.changed", {
          from: ev.from ?? "",
          to: ev.to,
          source: ev.source,
        });
      });
    }
    this.cameras.main.setBackgroundColor(PALETTE.hudPanel);

    this.identityService =
      (this.registry.get("authorIdentityService") as AuthorIdentityService | undefined) ??
      new AuthorIdentityService();
    if (!this.registry.get("authorIdentityService")) {
      this.registry.set("authorIdentityService", this.identityService);
    }

    const cx = NATIVE_WIDTH / 2;

    // Participant-facing GAME title: "Knight Errand" (confirmed by
    // Jonathan 2026-05-17). The hero is still named "Roamling" inside
    // the world; "Roamling" is also the project codename (PRD §1).
    this.add
      .text(cx, 18, "K N I G H T  E R R A N D", {
        fontFamily: "monospace",
        fontSize: "12px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0.5, 0.5);
    this.add
      .text(cx, 30, t("launcher.subtitle"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);

    this.buildLanguageToggle();

    this.add
      .text(40, 54, t("launcher.participant"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);

    this.add
      .text(40, 78, t("launcher.variant"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);

    this.variantA = this.add
      .rectangle(120, 78, 88, 26, PALETTE.ground)
      .setOrigin(0, 0.5)
      .setStrokeStyle(2, PALETTE.heroOutline);
    this.variantALabel = this.add
      .text(164, 78, t("launcher.variant.a"), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.variantA, () => this.setVariant("A"));

    this.variantB = this.add
      .rectangle(216, 78, 88, 26, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.variantBLabel = this.add
      .text(260, 78, t("launcher.variant.b"), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.variantB, () => this.setVariant("B"));

    this.buildMarketTile();
    this.buildProfileBadge();
    this.buildDebugCheckbox();

    // PLAY — primary action. Same size as MARKET and on the same row, just
    // left of it; `paintVariant` sets the final X so the pair stays centred.
    this.startButton = this.add
      .rectangle(cx, NATIVE_HEIGHT - 38, BUTTON_WIDTH, BUTTON_HEIGHT, PALETTE.geo)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, PALETTE.heroOutline);
    this.startLabel = this.add
      .text(cx, NATIVE_HEIGHT - 38, t("launcher.start"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.toHex(0x000000),
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.startButton, () => this.startSession());

    this.add
      .text(cx, NATIVE_HEIGHT - 14, t("launcher.hint"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);

    this.buildFocusRing();
    this.paintVariant();

    // Re-render the entire launcher on language change. Cheaper than rebinding
    // every text object; scene is short-lived. `shutdown` unsubscribes.
    this.langUnsubscribe = LanguageService.onChange(() => this.scene.restart());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.langUnsubscribe?.();
      this.langUnsubscribe = undefined;
    });
  }

  /**
   * Top-right DE / EN chip pair. Active language renders highlighted (geo gold
   * border), inactive renders dim. Click flips LanguageService, which fires
   * the change event and triggers a scene restart so every other label picks
   * up the new strings.
   */
  private buildLanguageToggle(): void {
    const baseX = NATIVE_WIDTH - 56;
    const y = 12;
    const chipW = 24;
    const chipH = 14;

    this.langChipDe = this.add
      .rectangle(baseX, y, chipW, chipH, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.langChipDeLabel = this.add
      .text(baseX + chipW / 2, y, "DE", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudDim),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.langChipDe, () => this.setLanguage("de"));

    this.langChipEn = this.add
      .rectangle(baseX + chipW + 2, y, chipW, chipH, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.langChipEnLabel = this.add
      .text(baseX + chipW + 2 + chipW / 2, y, "EN", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudDim),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.langChipEn, () => this.setLanguage("en"));

    this.paintLanguageChips();
  }

  private setLanguage(lang: Language): void {
    if (lang === LanguageService.getLanguage()) return;
    const previous = LanguageService.getLanguage();
    LanguageService.setLanguage(lang, "launcher");
    this.telemetry.emit(1, "i18n.language.changed", {
      from: previous,
      to: lang,
      source: "launcher",
    });
  }

  private paintLanguageChips(): void {
    const lang = LanguageService.getLanguage();
    const de = lang === "de";
    this.langChipDe.fillColor = de ? PALETTE.ground : PALETTE.hudPanel;
    this.langChipDe.setStrokeStyle(de ? 2 : 1, de ? PALETTE.geo : PALETTE.hudDim);
    this.langChipDeLabel.setColor(this.toHex(de ? PALETTE.hudText : PALETTE.hudDim));

    this.langChipEn.fillColor = !de ? PALETTE.ground : PALETTE.hudPanel;
    this.langChipEn.setStrokeStyle(!de ? 2 : 1, !de ? PALETTE.geo : PALETTE.hudDim);
    this.langChipEnLabel.setColor(this.toHex(!de ? PALETTE.hudText : PALETTE.hudDim));
  }

  override update(): void {
    const s = this.controls.read();

    // WASD / arrows walk the focus ring (up/left = prev, down/right = next).
    const dy = s.moveY > 0.5 ? 1 : s.moveY < -0.5 ? -1 : 0;
    if (dy !== this.prevMoveY) {
      this.prevMoveY = dy;
      if (dy !== 0) this.moveFocus(dy);
    }
    const dx = s.moveX > 0.5 ? 1 : s.moveX < -0.5 ? -1 : 0;
    if (dx !== this.prevMoveX) {
      this.prevMoveX = dx;
      if (dx !== 0) this.moveFocus(dx);
    }

    // J / K / Space (+ gamepad A/X/Y/Start) activate the focused element.
    if (s.interactPressed || s.attackPressed || s.jumpPressed || s.startPressed) {
      this.focusItems[this.focusIdx]?.activate();
    }
  }

  private setVariant(v: Variant): void {
    if (v === this.selectedVariant) return;
    this.selectedVariant = v;
    this.telemetry.emit(1, "ui.variant_select", { variant: v });
    this.paintVariant();
  }

  private paintVariant(): void {
    const aSelected = this.selectedVariant === "A";
    this.variantA.fillColor = aSelected ? PALETTE.ground : PALETTE.hudPanel;
    this.variantA.setStrokeStyle(aSelected ? 2 : 1, aSelected ? PALETTE.heroOutline : PALETTE.hudDim);
    this.variantALabel.setColor(this.toHex(aSelected ? PALETTE.hudText : PALETTE.hudDim));

    this.variantB.fillColor = !aSelected ? PALETTE.ground : PALETTE.hudPanel;
    this.variantB.setStrokeStyle(!aSelected ? 2 : 1, !aSelected ? PALETTE.heroOutline : PALETTE.hudDim);
    this.variantBLabel.setColor(this.toHex(!aSelected ? PALETTE.hudText : PALETTE.hudDim));

    // MARKET is Variant-B only. PLAY sits left of it as a centred pair; when
    // MARKET is hidden (Variant A), PLAY re-centres on its own.
    const marketVisible = this.selectedVariant === "B";
    if (this.marketTile) this.marketTile.setVisible(marketVisible);
    if (this.startButton) {
      const playX = marketVisible ? NATIVE_WIDTH / 2 - BUTTON_GROUP_OFFSET : NATIVE_WIDTH / 2;
      this.startButton.setX(playX);
      this.startLabel.setX(playX);
    }
    // Keep the focus cursor on a visible element and tracking PLAY's new X.
    if (this.focusItems.length > 0) {
      if (!this.focusItems[this.focusIdx]!.visible()) this.moveFocus(1);
      else this.repaintFocusCursor();
    }
  }

  // -- focus ring -----------------------------------------------------------

  /**
   * Build the focus ring over every interactive element. Items use closures
   * so they track runtime changes (PLAY's X, MARKET's visibility). Initial
   * focus is PLAY so a quick Space / Start launches straight away.
   */
  private buildFocusRing(): void {
    this.focusItems = [
      { center: () => ({ x: this.langChipDe.x + 12, y: this.langChipDe.y }), size: { w: 24, h: 14 }, visible: () => true, activate: () => this.setLanguage("de") },
      { center: () => ({ x: this.langChipEn.x + 12, y: this.langChipEn.y }), size: { w: 24, h: 14 }, visible: () => true, activate: () => this.setLanguage("en") },
      { center: () => ({ x: this.profileBadge.container.x + 32, y: this.profileBadge.container.y + 7 }), size: { w: 64, h: 14 }, visible: () => true, activate: () => this.openProfile() },
      { center: () => ({ x: this.variantA.x + 44, y: this.variantA.y }), size: { w: 88, h: 26 }, visible: () => true, activate: () => this.setVariant("A") },
      { center: () => ({ x: this.variantB.x + 44, y: this.variantB.y }), size: { w: 88, h: 26 }, visible: () => true, activate: () => this.setVariant("B") },
      { center: () => ({ x: this.debugCheckbox.x + 5, y: this.debugCheckbox.y }), size: { w: 14, h: 14 }, visible: () => true, activate: () => this.toggleDebug() },
      { center: () => ({ x: this.startButton.x, y: this.startButton.y }), size: { w: BUTTON_WIDTH, h: BUTTON_HEIGHT }, visible: () => true, activate: () => this.startSession() },
      { center: () => ({ x: this.marketTile.x, y: this.marketTile.y }), size: { w: BUTTON_WIDTH, h: BUTTON_HEIGHT }, visible: () => this.selectedVariant === "B", activate: () => this.openShop() },
    ];
    this.focusIdx = this.focusItems.length - 2; // PLAY
    this.repaintFocusCursor();
  }

  /** Advance focus by `dir`, wrapping and skipping hidden items. */
  private moveFocus(dir: number): void {
    const n = this.focusItems.length;
    if (n === 0) return;
    let i = this.focusIdx;
    for (let step = 0; step < n; step++) {
      i = Phaser.Math.Wrap(i + dir, 0, n);
      if (this.focusItems[i]!.visible()) break;
    }
    this.focusIdx = i;
    this.repaintFocusCursor();
  }

  /** Move (recreate) the outline cursor around the focused element. */
  private repaintFocusCursor(): void {
    const item = this.focusItems[this.focusIdx];
    if (!item) return;
    const c = item.center();
    this.focusCursor?.destroy();
    this.focusCursor = this.add
      .rectangle(c.x, c.y, item.size.w + 6, item.size.h + 6)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(0.5, 0.5)
      .setDepth(100);
  }

  /**
   * Variant-B-only MARKET tile that opens the shop overlay between sessions.
   * Wallet trades happen on a persistent launcher snapshot; carry-over into
   * the runner is a separate TODO (see design doc §3 / SessionConfig).
   */
  private buildMarketTile(): void {
    // Same footprint as PLAY, on the same row just to its right. Keeps its
    // UGC-cyan border + corner-mark so it still reads as the UGC entry.
    const cx = NATIVE_WIDTH / 2;
    this.marketTile = this.add.container(cx + BUTTON_GROUP_OFFSET, NATIVE_HEIGHT - 38);
    const bg = this.add
      .rectangle(0, 0, BUTTON_WIDTH, BUTTON_HEIGHT, PALETTE.shopWall, 1)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(2, UGC_CYAN);
    const banner = this.add
      .text(0, 0, t("launcher.market.title"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.toHex(PALETTE.parchment),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    const corner = this.add
      .rectangle(BUTTON_WIDTH / 2 - 6, -BUTTON_HEIGHT / 2 + 2, 4, 4, UGC_CYAN, 1)
      .setOrigin(0, 0);
    this.marketTile.add([bg, banner, corner]);
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerdown", () => {
      this.telemetry.emit(1, "ugc.shop.button.click", { source: "launcher_tile" });
      this.openShop();
    });
    this.marketTile.setVisible(this.selectedVariant === "B");
  }

  private openShop(): void {
    // Stash telemetry into the registry so ShopScene can find it without
    // needing a back-reference to LauncherScene.
    this.registry.set("telemetryService", this.telemetry);
    this.scene.start("ShopScene", { returnTo: "launcher", walletSource: "launcher" });
  }

  /**
   * Compact player-profile chip (Jonathan 2026-05-21), shown as the value of
   * the "Participant" row in the same `AuthorBadge` style used in-game / in
   * the market: avatar + chosen name + cyan corner. Click — or press X / J —
   * opens the profile editor. The avatar is the player's generated identity
   * (circle) or, when they've assigned a custom photo, that image (square).
   */
  private buildProfileBadge(): void {
    const profile = ProfileService.get();
    this.profileBadge = new AuthorBadge(this, 120, 47, this.identityService);
    this.applyProfileToBadge(profile);
    this.profileBadge.setOnClick(() => this.openProfile());
  }

  private applyProfileToBadge(profile: PlayerProfile): void {
    if (profile.avatarKind === "asset") {
      const asset = AssetService.getUploaded();
      if (asset) {
        const key = AssetService.uploadedThumbnailKey(this, asset, 32, 32);
        this.profileBadge.setPlayer(profile.name, { kind: "image", textureKey: key });
        return;
      }
    }
    this.profileBadge.setPlayer(profile.name, {
      kind: "identity",
      identity: this.identityService.forSeed(profile.avatarSeed),
    });
  }

  private openProfile(): void {
    this.registry.set("telemetryService", this.telemetry);
    this.telemetry.emit(1, "profile.button.click", { source: "launcher_tile" });
    this.scene.start("ProfileScene", { returnTo: "launcher" });
  }

  /**
   * "Debug" row, laid out like the Participant / Variant rows: a left-column
   * label + a right-column control (checkbox + shortcut hint). Lit when the
   * developer shortcuts (1-9 depth jump, fast-forward) are armed. Defaults to
   * `DEFAULT_DEBUG_MODE` (read from `VITE_DEBUG_MODE` at build time) so
   * participants see an unchecked box on a clean checkout while the developer
   * sees it pre-checked locally. The label stays English (dev-only, excluded
   * from i18n by spec).
   */
  private buildDebugCheckbox(): void {
    const y = 102;
    this.add
      .text(40, y, "Debug", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    this.debugCheckbox = this.add
      .rectangle(120, y, 10, 10, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.debugCheckmark = this.add
      .text(125, y, "x", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.geo),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.debugLabel = this.add
      .text(136, y, "1-9 depth · → fast-forward", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    this.makeClickable(this.debugCheckbox, () => this.toggleDebug());
    // Clicking the label is a natural affordance too; reuse the same handler.
    this.debugLabel.setInteractive({ useHandCursor: true });
    this.debugLabel.on("pointerdown", () => this.toggleDebug());
    this.paintDebugCheckbox();
  }

  private toggleDebug(): void {
    this.debugMode = !this.debugMode;
    this.telemetry.emit(1, "ui.debug_toggle", { enabled: this.debugMode });
    this.paintDebugCheckbox();
  }

  private paintDebugCheckbox(): void {
    this.debugCheckmark.setVisible(this.debugMode);
    this.debugLabel.setColor(
      this.toHex(this.debugMode ? PALETTE.hudText : PALETTE.hudDim),
    );
    this.debugCheckbox.setStrokeStyle(
      this.debugMode ? 2 : 1,
      this.debugMode ? PALETTE.geo : PALETTE.hudDim,
    );
  }

  private startSession(): void {
    const cfg: SessionConfig = {
      variant: this.selectedVariant,
      participantId: this.participantId,
      startedAt: performance.now(),
      backgroundId: DEFAULT_BACKGROUND_ID,
      debugMode: this.debugMode,
      language: LanguageService.getLanguage(),
      // Variant-B-only: forward any Launcher-side Market trades into the
      // runner. The snapshot lives in the registry across MARKET visits;
      // we copy it (not pass by reference) so the runner can mutate its
      // own copy without affecting future Launcher visits.
      startingWallet: this.snapshotForStart(),
    };
    this.telemetry.emit(1, "ui.start_pressed", {
      variant: cfg.variant,
      backgroundId: cfg.backgroundId ?? DEFAULT_BACKGROUND_ID,
      debug: cfg.debugMode === true,
      language: cfg.language ?? "de",
    });
    this.telemetry.flush();
    this.scene.start("tutorial", cfg);
  }

  /** Build the runner's starting wallet from the Launcher-side Market snapshot.
   *  Returns undefined when there's been no trading (so the runner takes its
   *  default fresh-session values). */
  private snapshotForStart(): SessionConfig["startingWallet"] {
    if (this.selectedVariant !== "B") return undefined;
    const snap = this.registry.get("launcherWalletSnapshot") as WalletSnapshot | undefined;
    if (!snap) return undefined;
    return {
      geo: snap.geo,
      hp: snap.hp,
      featherCharges: snap.featherCharges,
      inventory: snap.inventory.map((i) => ({ kind: i.kind, pickedUpAtMs: i.pickedUpAtMs })),
      doubleJump: snap.skills.doubleJump,
      escapeFeather: snap.skills.escapeFeather,
    };
  }

  private toHex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }

  /** Wire pointerdown on a UI rect with hand-cursor feedback. */
  private makeClickable(rect: Phaser.GameObjects.Rectangle, onClick: () => void): void {
    rect.setInteractive({ useHandCursor: true });
    rect.on("pointerdown", () => {
      this.telemetry.emit(1, "ui.click", { x: Math.round(rect.x), y: Math.round(rect.y) });
      onClick();
    });
  }
}
